/**
 * VerifyCodeScreen - Enter 6-digit verification code
 *
 * Simple wireframe UI - easy to replace later
 * User enters code received via email
 * Manages two-modal welcome flow for first-time logins
 *
 * Handles two flows:
 * - Normal user → redirect to /welcome
 * - Applicant (email verified, no profile) → redirect to /apply
 */

import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'

interface VerifyCodeScreenProps {
  email: string
  onBack: () => void
}

export function VerifyCodeScreen({ email, onBack }: VerifyCodeScreenProps) {
  const navigate = useNavigate()
  const { login, error, clearError, isLoading } = useAuth()
  const [code, setCode] = useState('')
  const [localError, setLocalError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    // Validate code
    if (!code || code.length !== 6) {
      setLocalError('Please enter the 6-digit code')
      return
    }

    try {
      const result = await login(email, code)

      // ======================================================================
      // APPLICANT FLOW: Email verified but no profile
      // Redirect to /apply where they'll complete their application
      // ======================================================================
      if (result.isApplicant) {
        navigate('/apply')
        return
      }

      // ======================================================================
      // NORMAL USER FLOW: Profile exists
      // Check if user came from /vip and redirect back there
      // ======================================================================
      const params = new URLSearchParams(window.location.search)
      const returnTo = params.get('returnTo')
      if (returnTo === '/vip') {
        navigate('/vip', { replace: true })
        return
      }
      navigate('/welcome')
    } catch (err) {
      // Error is already set in context
      console.error('[VerifyCodeScreen] ❌ Failed to verify code:', err)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <div className="min-h-screen flex items-center justify-center forest-login-bg px-6">
      <div className="relative z-10 max-w-md w-full space-y-7 bg-braydin-ink/45 backdrop-blur-md rounded-2xl shadow-xl p-8 border border-white/10">
        {/* Header */}
        <div className="text-center animate-slide-up">
          <h1 className="font-heading font-bold text-2xl tracking-wide text-braydin-cream">
            Enter your code
          </h1>
          <p className="mt-2 text-sm text-white/70">
            We sent a 6-digit code to
          </p>
          <p className="font-medium text-braydin-sage">
            {email}
          </p>
        </div>

        {/* Verify Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 animate-slide-up animate-delay-100">
          <div>
            <label htmlFor="code" className="block text-xs font-medium tracking-wide text-white/80 mb-1.5">
              Verification code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength={6}
              disabled={isLoading}
              className="block w-full px-3 py-3 text-center text-2xl tracking-[0.5em] rounded-xl bg-braydin-cream/95 text-braydin-ink placeholder-braydin-taupe border border-white/20 focus:outline-none focus:ring-2 focus:ring-braydin-olive"
            />
          </div>

          {/* Error messages */}
          {(localError || error) && (
            <div className="text-red-300 text-sm text-center animate-fade-in">
              {localError || error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full py-3.5 rounded-xl bg-braydin-olive hover:bg-braydin-oliveDark text-braydin-cream font-heading font-semibold tracking-[0.2em] pl-[0.2em] text-sm btn-hover disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
          >
            {isLoading ? 'VERIFYING...' : 'VERIFY CODE'}
          </button>

          {/* Back button */}
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="w-full py-2.5 rounded-xl border border-white/30 text-white/80 hover:bg-white/10 text-sm transition-smooth disabled:opacity-50"
          >
            Use a different email
          </button>
        </form>

        {/* Helper text */}
        <p className="text-center text-xs text-white/60">
          Didn't receive the code? Check your spam folder or try again
        </p>

        {/* Support button */}
        <div className="text-center">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-[11px] font-heading tracking-[0.22em] pl-[0.22em] text-white/90 border border-white/40 hover:border-white/70 hover:bg-white/10 rounded-full transition-all"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            SUPPORT
          </a>
        </div>
      </div>
    </div>
  )
}
